import { useCallback, useEffect, useRef, useState } from 'react';

import { getPostHogClient, initPostHog } from 'lib/posthog/client';

/**
 * Hook to consume a PostHog experiment (A/B test).
 *
 * @param {string} flagKey - The feature flag key configured in PostHog
 * @returns {{ variant: string | boolean | undefined, isLoading: boolean }}
 *
 * @example
 * const { variant, isLoading } = usePostHogExperiment('cupons-otimizacao-seo');
 *
 * if (isLoading) return <OriginalComponent />;
 * if (variant === 'test') return <OptimizedComponent />;
 * return <OriginalComponent />;
 */
const usePostHogExperiment = (flagKey) => {
  const [variant, setVariant] = useState(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);

  const handleFlagsLoaded = useCallback(() => {
    if (!mountedRef.current) return;

    const posthog = getPostHogClient();

    if (!posthog) {
      setIsLoading(false);
      return;
    }

    const flagValue = posthog.getFeatureFlag(flagKey);
    setVariant(flagValue);
    setIsLoading(false);
  }, [flagKey]);

  useEffect(() => {
    mountedRef.current = true;
    let unsubscribe;

    const setup = (posthog) => {
      if (!mountedRef.current) return;

      if (!posthog) {
        setIsLoading(false);
        return;
      }

      // Check if flags are already loaded
      const currentValue = posthog.getFeatureFlag(flagKey);

      if (currentValue !== undefined) {
        setVariant(currentValue);
        setIsLoading(false);
        return;
      }

      // Wait for flags to load; capture unsubscribe for cleanup
      unsubscribe = posthog.onFeatureFlags(handleFlagsLoaded);
    };

    // If SDK is already loaded, use it immediately
    const client = getPostHogClient();

    if (client) {
      setup(client);
    } else {
      // SDK still loading — wait for it
      initPostHog().then(setup);
    }

    return () => {
      mountedRef.current = false;

      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [flagKey, handleFlagsLoaded]);

  return {
    variant,
    isLoading,
  };
};

export default usePostHogExperiment;
